/* apps/web/src/branding/Brand.tsx
 *
 * The only components allowed to reference a logo asset. Sized by max-height /
 * max-width so a customer logo of any proportion fits without redesign.
 * Third-party identities (Microsoft, Copilot) are NOT customer-brandable and
 * live in their own components — never route them through these.
 */
import { useBranding } from "./brandingStore";

/** `light` picks the reversed asset, for a dark surface (sidebar, hero panel). */
type BrandTone = "dark" | "light";

interface LogoProps {
  /** Cap for the horizontal logo. Default suits the sidebar header. */
  maxHeight?: number;
  maxWidth?: number;
  tone?: BrandTone;
  className?: string;
  /** Overrides the product name as the accessible name. */
  alt?: string;
}

/** Full horizontal logo. Falls back to the product name if no asset exists. */
export function BrandLogo({
  maxHeight = 24,
  maxWidth = 176,
  tone = "dark",
  className,
  alt,
}: LogoProps): JSX.Element {
  const branding = useBranding();
  const src = tone === "light" ? branding.logoLight || branding.logo : branding.logo;
  if (!src) {
    return <span className={className} style={styles.wordmark}>{branding.brandName}</span>;
  }
  return (
    <img
      className={className}
      src={src}
      alt={alt ?? branding.brandName}
      style={{ display: "block", maxHeight, maxWidth, width: "auto", height: "auto", objectFit: "contain" }}
    />
  );
}

/** Compact square mark: collapsed sidebar, login card, mobile header. */
export function BrandMark({
  size = 26,
  tone = "dark",
  className,
}: { size?: number; tone?: BrandTone; className?: string }): JSX.Element {
  const branding = useBranding();
  const src = tone === "light" ? branding.logoMarkLight || branding.logoMark : branding.logoMark;
  if (!src) {
    return (
      <span className={className} style={{ ...styles.markFallback, width: size, height: size, fontSize: size * 0.46 }}>
        {branding.logoMarkInitials}
      </span>
    );
  }
  return (
    <img
      className={className}
      src={src}
      alt=""
      aria-hidden
      /* Capped rather than sized: a customer mark is rarely square, and the
         canvas fits every logo by max-height/max-width so a wide one keeps its
         proportions instead of being squashed into a box. */
      style={{
        display: "block",
        maxHeight: size,
        maxWidth: size * 2,
        width: "auto",
        height: "auto",
        objectFit: "contain",
      }}
    />
  );
}

/** Mark + name, the sidebar/login lockup. */
export function BrandLockup({
  compact = false,
  tone = "dark",
}: { compact?: boolean; tone?: BrandTone }): JSX.Element {
  const branding = useBranding();
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <BrandMark tone={tone} />
      {!compact && <span style={styles.wordmark}>{branding.brandName}</span>}
    </span>
  );
}

const styles = {
  wordmark: {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: "-0.012em",
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  markFallback: {
    flex: "0 0 auto",
    display: "grid",
    placeItems: "center",
    borderRadius: 6,
    background: "var(--brand-primary)",
    color: "var(--text-on-brand)",
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
} as const;
